import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ConsumerLayout } from '@/components/consumer/ConsumerLayout';
import { useSolicitacaoConsumer, useAceitarOrcamento, useCancelarSolicitacao } from '@/hooks/useSolicitacoes';
import { getFotoSolicitacaoUrl } from '@/services/solicitacoes.service';
import { estadoSolicitacao, estadoOrcamento } from '@/lib/solicitacoes';
import { formatarMoeda } from '@/lib/currency';

function FotoPedido({ path }: { path: string }) {
  const { data: url } = useQuery({
    queryKey: ['solicitacao-foto', path],
    queryFn: () => getFotoSolicitacaoUrl(path),
    staleTime: 50 * 60 * 1000,
  });
  if (!url) return <div className="h-20 w-20 rounded bg-muted animate-pulse" />;
  return <img src={url} alt="" className="h-20 w-20 rounded object-cover" />;
}

export default function ConsumerPedidoDetalhe() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pedido, isLoading } = useSolicitacaoConsumer(id);
  const aceitar = useAceitarOrcamento();
  const cancelar = useCancelarSolicitacao();

  if (isLoading) {
    return <ConsumerLayout><div className="h-40 rounded-lg bg-muted animate-pulse" /></ConsumerLayout>;
  }
  if (!pedido) {
    return (
      <ConsumerLayout>
        <div className="flex flex-col items-center text-center gap-3 py-16">
          <p className="text-sm text-muted-foreground">{t('requests.notFound')}</p>
          <Link to="/app/pedidos" className="ow-btn ow-btn--outline">{t('myOrders.back')}</Link>
        </div>
      </ConsumerLayout>
    );
  }

  const est = estadoSolicitacao(pedido.status);
  const aberta = pedido.status === 'aberta';

  const onAceitar = async (orcamentoId: string) => {
    try {
      const clienteId = await aceitar.mutateAsync(orcamentoId);
      navigate(`/app/conversas/${clienteId}`);
    } catch {
      // erro já tratado (toast) no hook; não navega
    }
  };

  return (
    <ConsumerLayout>
      <div className="flex items-center gap-2 mb-4">
        <Link to="/app/pedidos" aria-label={t('myOrders.back')}><ArrowLeft className="h-5 w-5" /></Link>
      </div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-lg font-bold text-foreground">{pedido.categoria.nome}</h1>
        <span className={`text-xs px-2 py-0.5 rounded-full ${est.classe}`}>{t(est.labelKey)}</span>
      </div>
      <p className="text-sm text-muted-foreground">{pedido.cidade}</p>
      <p className="mt-2 text-foreground whitespace-pre-wrap">{pedido.descricao}</p>

      {pedido.fotos.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {pedido.fotos.map((p) => <FotoPedido key={p} path={p} />)}
        </div>
      )}

      <h2 className="mt-6 mb-2 font-semibold text-foreground">{t('requests.quotes')}</h2>
      {pedido.orcamentos.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('requests.noQuotes')}</p>
      ) : (
        <ul className="space-y-3">
          {pedido.orcamentos.map((o) => {
            const oe = estadoOrcamento(o.status);
            return (
              <li key={o.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  {o.empresa.logo_url
                    ? <img src={o.empresa.logo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                    : <div className="h-8 w-8 rounded-full bg-muted" />}
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{o.empresa.nome}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      {o.empresa.rating_total > 0
                        ? <><Star className="h-3 w-3 fill-current" /> {o.empresa.rating_media} · {t('requests.ratingCount', { count: o.empresa.rating_total })}</>
                        : t('requests.ratingNone')}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${oe.classe}`}>{t(oe.labelKey)}</span>
                </div>
                <div className="mt-2 text-lg font-bold text-foreground">{formatarMoeda(o.valor)}</div>
                {o.mensagem && <p className="text-sm text-muted-foreground mt-1">{o.mensagem}</p>}
                {aberta && (
                  <button
                    type="button"
                    onClick={() => onAceitar(o.id)}
                    disabled={aceitar.isPending}
                    className="ow-btn ow-btn--primary mt-2 disabled:opacity-50"
                  >
                    {t('requests.accept')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {aberta && (
        <button
          type="button"
          onClick={() => cancelar.mutate(pedido.id)}
          disabled={cancelar.isPending}
          className="ow-btn ow-btn--ghost mt-6 text-destructive disabled:opacity-50"
        >
          {t('requests.cancel')}
        </button>
      )}
    </ConsumerLayout>
  );
}
